import { useParams, useSearchParams } from 'react-router-dom'

import type { TrackedCompetition } from '@/api/market'
import { useNotLivePage } from '@/app/notLive'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, DataTable, EmptyState, formatCurrency, formatInteger, LoadingBlock, type Column, type FormatOptions } from '@/design'
import { formatDateKey } from '@/lib/filters'

import { TrackedStatusBadge } from './badges'
import { competitorColumns } from './columns'
import { useMarketQuery, useMoney } from './hooks'
import { NotLiveSection } from './NotLiveSection'

function trackedColumns(money: FormatOptions): Column<TrackedCompetition>[] {
  return [
    { key: 'prize', header: 'Prize', sortValue: (row) => row.prize, cell: (row) => row.prize },
    { key: 'category', header: 'Category', cell: (row) => row.category },
    { key: 'price', header: 'Ticket price', align: 'end', sortValue: (row) => row.ticketPrice, cell: (row) => formatCurrency(row.ticketPrice, money) },
    { key: 'max', header: 'Max entries', align: 'end', sortValue: (row) => row.maxEntries, cell: (row) => formatInteger(row.maxEntries) },
    { key: 'instant', header: 'Instant wins', align: 'end', sortValue: (row) => row.instantWins, cell: (row) => formatInteger(row.instantWins) },
    { key: 'starts', header: 'Starts', sortValue: (row) => row.startDate, cell: (row) => formatDateKey(row.startDate, true) },
    { key: 'ends', header: 'Ends', sortValue: (row) => row.endDate, cell: (row) => formatDateKey(row.endDate, true) },
    { key: 'status', header: 'Status', cell: (row) => <TrackedStatusBadge status={row.status} /> },
    { key: 'promo', header: 'Promotional messaging', cell: (row) => row.promotionalMessaging || '—' },
  ]
}

/** One competitor's profile, its tracked competitions, and — with `?compare=` — a side-by-side view against others. */
export function CompetitorDetailPage({ route }: { route: RouteDef }) {
  const notLive = useNotLivePage('marketCompetitors')
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const compareIds = (searchParams.get('compare') ?? '').split(',').filter(Boolean)
  const money = useMoney()

  const competitors = useMarketQuery('competitors', (client, filters) => client.marketCompetitors(filters))
  const tracked = useMarketQuery('tracked-competitions', (client, filters) => client.marketTrackedCompetitions(filters))

  const competitor = competitors.data?.data.find((candidate) => candidate.id === id)
  const compared = id
    ? (competitors.data?.data.filter((candidate) => candidate.id === id || compareIds.includes(candidate.id)) ?? [])
    : []
  const theirCompetitions = tracked.data?.data.filter((candidate) => candidate.competitorId === id) ?? []

  if (notLive) return <NotLiveSection route={route} />

  if (competitors.isPending || tracked.isPending) {
    return (
      <SectionFrame route={route}>
        <LoadingBlock rows={6} />
      </SectionFrame>
    )
  }

  if (!competitor) {
    return (
      <SectionFrame route={route}>
        <Card>
          <EmptyState variant="no-results" title="Competitor not found" description="It may have been removed, or the link is out of date." />
        </Card>
      </SectionFrame>
    )
  }

  return (
    <SectionFrame route={route} title={competitor.name} description={`${competitor.domain} · ${competitor.brand}`}>
      <div className="ci-stack">
        {compared.length > 1 ? (
          <Card title="Side-by-side comparison" subtitle="Selected on the competitors list.">
            <DataTable
              caption="Competitor comparison"
              columns={competitorColumns(money)}
              rows={compared}
              rowKey={(row) => row.id}
              initialSort={{ key: 'price', direction: 'desc' }}
            />
          </Card>
        ) : null}

        <Card title="Tracked competitions" subtitle={`${theirCompetitions.length} tracked from ${competitor.name}`}>
          <DataTable
            caption={`${competitor.name}'s tracked competitions`}
            columns={trackedColumns(money)}
            rows={theirCompetitions}
            rowKey={(row) => row.id}
            initialSort={{ key: 'status', direction: 'asc' }}
            empty={<EmptyState variant="no-data" compact title="No tracked competitions yet" />}
          />
        </Card>
      </div>
    </SectionFrame>
  )
}
